import { useDebounce } from 'ahooks';
import { uniqBy } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { toChatModalLead } from '../../components/AdvanceSearch/AdvanceSearchModal.functions';
import {
  leadActiveChangedEvent,
  tLeadActiveChange,
} from '../../components/Chat/leadActiveChanged.event';
import { toLeadChatModalLead } from '../../components/ChatModal/useOpenLeadChat.hook';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context';
import { useSocketsContext } from '../../providers/SocketsProvider/Sockets.context';
import {
  tLeadCreatedEvent,
  tLeadUpdatedEvent,
} from '../../providers/SocketsProvider/Sockets.types';
import { eAlertType } from '../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../providers/ToastProvider/Toast.hook';
import { useUserContext } from '../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context';
import { MessengerContext } from './Messenger.context';
import { eChatLeadsTabs } from './Messenger.enum';
import {
  chatLeadParser,
  createArchiveFrameOrder,
  fitsActiveTab,
  frameActiveChange,
  isMarkOnlyFrame,
  isUndeliveredFilterRefused,
  placeInActivityOrder,
  placeInUndeliveredTab,
  resolveDeepLinkIntegration,
  UNDELIVERED_COUNT_REFRESH_MS,
} from './Messenger.functions';
import { MessengerService } from './Messenger.service';
import { tChatLead, tMessengerContext } from './Messenger.types';
import {
  hasLeadLeftLeadbotSession,
  hidesLeadFromViewer,
} from '../../functions/leadbotSession.functions';
import { useLoadedFeatureFlag } from '../../hooks/featureFlags/useLoadedFeatureFlag.hook';

export { UNDELIVERED_COUNT_REFRESH_MS } from './Messenger.functions';

/**
 * BUG-1127: `row` is the lead as a frame described it, so a list answer that
 * predates the frame can still show a lead that moved into the open tab.
 */
type tActiveChange = tLeadActiveChange & { count: number; row?: tChatLead };

/** The rows with one lead's `active` set; the same array when nothing differs. */
function withLeadActive(leads: tChatLead[], change: tLeadActiveChange): tChatLead[] {
  return leads.some(
    (lead) => lead.id === change.id && lead.active !== change.active,
  )
    ? leads.map((lead) =>
        lead.id === change.id ? { ...lead, active: change.active } : lead,
      )
    : leads;
}

type tUndeliveredCountState = {
  workspaceId: string | undefined;
  /** Null when unknown: not asked yet, or the count could not be read (424). */
  count: number | null;
  /** The workspace refused the filter (422: projection-backed leads). */
  unsupported: boolean;
};

export const useMessengerContext = (): tMessengerContext => {
  const context = useContext(MessengerContext);

  if (context === undefined) {
    throw new Error(
      'useMessengerContext must be used within a MessengerProvider',
    );
  }

  return context;
};

export default function useMessenger(): tMessengerContext {
  const { addAlert } = useToastContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { currentUserId, currentUserRole } = useUserContext();
  const { leadUpdatedEvent, leadDeletedEvent, catchUpEvent } = useSocketsContext();
  const { selectedLeadForChatModal, setSelectedLeadForChatModal, setChatModalIsOpen, setMaximizeModal } =
    useModalsContext();

  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [leads, setLeads] = useState<tChatLead[]>([]);
  // The search text `leads` currently answers. Set with the list, not with the
  // input, so a consumer can tell "these rows are for what is typed" from
  // "these rows are left over from before the last keystroke".
  const [leadsQuery, setLeadsQuery] = useState('');
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [isLastPage, setIsLastPage] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const leadsInputField = useDebounce(inputValue, { wait: 400 });
  const queryParams = new URLSearchParams(location.search);
  const leadId = queryParams.get('leadId');
  const integration = queryParams.get('integration');
  const showAssigned = queryParams.get('showAssigned') === 'true';

  const [selectedTab, setSelectedTab] = useState<eChatLeadsTabs>(
    showAssigned ? eChatLeadsTabs.ASSIGNED_TO_ME : eChatLeadsTabs.ALL,
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRequestIdRef = useRef(0);
  const deletedDuringRequestRef = useRef(new Set<string>());
  const loadingMoreRef = useRef(false);
  // BUG-1126: every archive/activation from the chat menu is counted, so a
  // list request already in flight when it happened cannot bring the old
  // value back. A request started after it answers with server truth.
  const activeChangeCountRef = useRef(0);
  // The latest change per lead: changing several leads while one request is
  // in flight must replay all of them, not only the last one.
  const activeChangesRef = useRef(new Map<string, tActiveChange>());
  // BUG-1127: archive frames can arrive inverted (parallel consumers); an
  // older one than the last seen for its lead is dropped.
  const isStaleArchiveFrameRef = useRef(createArchiveFrameOrder());
  // INT-1500 U14: "No entregados (N)", behind chat_undelivered-list.
  const isUndeliveredListEnabled = useLoadedFeatureFlag('chat_undelivered-list');
  const [undeliveredCountState, setUndeliveredCountState] =
    useState<tUndeliveredCountState>({
      workspaceId: undefined,
      count: null,
      unsupported: false,
    });
  const [undeliveredCountTick, setUndeliveredCountTick] = useState(0);
  const undeliveredCountThrottleRef = useRef<ReturnType<typeof setTimeout>>();
  // INT-1538 U7 (KTD9): the failure time of the last row the server paged to
  // in "No entregados", so a frame never shows a lead that belongs to a page
  // not loaded yet.
  const undeliveredOldestLoadedRef = useRef<Pick<
    tChatLead,
    'lastMessageFailedAt' | 'numericId'
  > | null>(null);
  // INT-1538 U7 (KTD9): a continuation of "No entregados" refused with a 422
  // (a cursor minted under the previous order) reloads page 1 once.
  const undeliveredContinuationReloadedRef = useRef(false);

  function shouldIncludeLeadSocketEvent(
    lead: tLeadUpdatedEvent | tLeadCreatedEvent,
  ) {
    if (
      selectedTab === eChatLeadsTabs.ASSIGNED_TO_ME &&
      lead.response.user?.auth_id !== currentUserId
    )
      return false;

    if (selectedTab === eChatLeadsTabs.ACTIVE && !lead.response.active)
      return false;

    if (selectedTab === eChatLeadsTabs.ARCHIVED && lead.response.active)
      return false;

    return true;
  }

  function buildQueryParams(cursor?: string) {
    return {
      paginationMode: 'cursor' as const,
      ...(cursor && { cursor }),
      ...(leadsInputField && { query: leadsInputField }),
      ...(selectedTab === eChatLeadsTabs.ASSIGNED_TO_ME && {
        assignedUserId: currentUserId,
      }),
      ...(selectedTab === eChatLeadsTabs.ACTIVE && {
        active: true,
      }),
      ...(selectedTab === eChatLeadsTabs.ARCHIVED && {
        active: false,
      }),
      ...(selectedTab === eChatLeadsTabs.UNREAD && {
        read: false,
      }),
      ...(selectedTab === eChatLeadsTabs.UNDELIVERED && {
        undelivered: 1 as const,
      }),
    };
  }

  function scheduleUndeliveredCountRefresh() {
    if (!isUndeliveredListEnabled || undeliveredCountThrottleRef.current) return;
    undeliveredCountThrottleRef.current = setTimeout(() => {
      undeliveredCountThrottleRef.current = undefined;
      setUndeliveredCountTick((tick) => tick + 1);
    }, UNDELIVERED_COUNT_REFRESH_MS);
  }

  leadDeletedEvent.useSubscription((event) => {
    const deletedIds = new Set(event.response);
    deletedIds.forEach((id) => deletedDuringRequestRef.current.add(id));
    scheduleUndeliveredCountRefresh();
    setLeads((previous) =>
      previous.some(({ id }) => deletedIds.has(id))
        ? previous.filter(({ id }) => !deletedIds.has(id))
        : previous,
    );
    if (
      selectedLeadForChatModal?.id &&
      deletedIds.has(selectedLeadForChatModal.id)
    ) {
      setChatModalIsOpen(false);
      setMaximizeModal(false);
      setSelectedLeadForChatModal(null);
    }
  });

  leadUpdatedEvent.useSubscription((socketEvent) => {
    // INT-1500 U14: a lead update may be the message that sets or clears a
    // "No entregados" mark; the count is asked again, throttled.
    scheduleUndeliveredCountRefresh();

    // BUG-1127: an archive frame older than one already applied for its lead
    // carries a state the lead has left; nothing in it is newer.
    const activeChange = frameActiveChange(socketEvent.response);
    if (activeChange && isStaleArchiveFrameRef.current(activeChange)) return;

    // BUG-1127 (Edgar on #3827): the frame's top-level `active` may be a
    // lagging snapshot. When its `changes` name `active`, that value is the
    // truth, so every row this frame builds or updates is painted with it.
    const event = activeChange
      ? { ...socketEvent, response: { ...socketEvent.response, active: activeChange.active } }
      : socketEvent;

    // BUG-1126: a server frame is assumed newer than the archive change kept
    // for its lead (not guaranteed: leads-service may build it from a lagging
    // replica), so a late list answer replays the frame's `active`. Runs
    // before the per-tab filters below on purpose: they decide which rows
    // this tab shows, not whether the frame is the newer state of the lead.
    const keptChange = activeChangesRef.current.get(event.response.uuid);
    if (keptChange && typeof event.response.active === 'boolean') {
      activeChangesRef.current.set(event.response.uuid, {
        ...keptChange,
        active: event.response.active,
      });
    }

    // BUG-1127: an archive or activation the frame announces is counted like
    // one from the chat menu, so a list answer read before it cannot bring
    // the old value back, nor the row back into a tab it left.
    if (activeChange) {
      activeChangeCountRef.current += 1;
      activeChangesRef.current.set(activeChange.id, {
        ...activeChange,
        count: activeChangeCountRef.current,
        row: chatLeadParser(
          event.response,
          leads.find((lead) => lead.id === activeChange.id),
        ),
      });
    }

    // BUG-892: the HTTP list hides SESSION_ACTIVE leads, so a held lead leaves the chat
    // list the same way it leaves the Pipe board. Level-triggered — an edge check missed
    // every steady-hold update and let the row back in.
    if (
      hidesLeadFromViewer({
        currentState: event.response.leadbot_state,
        currentUserRole,
      })
    ) {
      setLeads((prevLeads) =>
        prevLeads.filter((lead) => lead.id !== event.response.uuid),
      );
      return;
    }

    // BUG-1127: «Activos» and «Archivados» follow an archive or activation
    // at once: the row leaves the tab it no longer belongs to, and a lead
    // that now belongs comes in at its place in the order, never on top.
    if (
      activeChange &&
      (selectedTab === eChatLeadsTabs.ACTIVE || selectedTab === eChatLeadsTabs.ARCHIVED)
    ) {
      const movedLead = activeChangesRef.current.get(activeChange.id)?.row;
      setLeads((prevLeads) => {
        if (!fitsActiveTab(selectedTab, activeChange.active)) {
          return prevLeads.filter((lead) => lead.id !== activeChange.id);
        }
        return movedLead
          ? placeInActivityOrder(prevLeads, { lead: movedLead, isLastPage })
          : prevLeads;
      });
      if (!fitsActiveTab(selectedTab, activeChange.active)) return;
    }

    if (!shouldIncludeLeadSocketEvent(event)) return;

    // INT-1538 U7 (KTD9): "No entregados" is ordered by failure, like its
    // list. A frame places the lead by its failure time, or takes it out
    // when it lost the mark or was archived; activity never raises it.
    if (selectedTab === eChatLeadsTabs.UNDELIVERED) {
      setLeads((prevLeads) =>
        placeInUndeliveredTab(
          prevLeads,
          chatLeadParser(
            event.response,
            prevLeads.find((lead) => lead.id === event.response.uuid),
          ),
          undeliveredOldestLoadedRef.current,
          isLastPage,
        ),
      );
      return;
    }

    const { action_type: actions } = event;
    const updatedLead = event.response;
    const leftLeadbotSession = hasLeadLeftLeadbotSession({
      previousState: updatedLead.original?.leadbot_state,
      currentState: updatedLead.leadbot_state,
      stateChanged: Object.prototype.hasOwnProperty.call(
        updatedLead.changes ?? {},
        'leadbot_state',
      ),
    });
    const isUnreadTab = selectedTab === eChatLeadsTabs.UNREAD;
    const isRead = Boolean(updatedLead.message?.read);

    const isLeadUnreadUpdateEvent = isUnreadTab && !isRead;
    const isLeadStageUpdateEvent = !!actions?.includes('changed_stage_id');
    const isLeadAssigneeUpdateEvent = !!actions?.includes('changed_user_uuid');
    const isLeadMessageFromMeUpdateEvent = !!actions?.includes(
      'changed_last_message_from_me',
    );
    const isLeadMessageUpdateEvent = !!actions?.includes(
      'changed_last_message_on',
    );
    const isLeadActivatedEvent = !!actions?.includes('changed_active');
    const isLeadToInboxUpdateEvent = !updatedLead.funnel && !updatedLead.stage;

    // INT-1538 U7 (R11): a frame that only flips the mark reorders nothing.
    const leadShouldMoveAtTheTop =
      !isMarkOnlyFrame(updatedLead.changes) &&
      (isLeadStageUpdateEvent ||
        isLeadAssigneeUpdateEvent ||
        isLeadMessageUpdateEvent ||
        isLeadMessageFromMeUpdateEvent ||
        isLeadActivatedEvent ||
        isLeadToInboxUpdateEvent ||
        isLeadUnreadUpdateEvent);

    setLeads((prevLeads) => {
      let nextLeads = [...prevLeads];
      // Exclude lead from unread tab if it is read
      if (isUnreadTab && isRead) {
        nextLeads = nextLeads.filter((lead) => lead.id !== updatedLead.uuid);
      }

      const insertRevealedLead =
        leftLeadbotSession &&
        !(isUnreadTab && isRead) &&
        !nextLeads.some((lead) => lead.id === updatedLead.uuid);

      if (leadShouldMoveAtTheTop || insertRevealedLead) {
        const prevLead = nextLeads.find((lead) => lead.id === updatedLead.uuid);
        const leadForTheTop = prevLead
          ? chatLeadParser(updatedLead, prevLead)
          : chatLeadParser(updatedLead);

        return [
          leadForTheTop,
          ...nextLeads.filter((lead) => lead.id !== updatedLead.uuid),
        ];
      }

      return nextLeads.map((lead) => {
        if (lead.id === updatedLead.uuid) {
          return chatLeadParser(updatedLead, lead);
        }

        return lead;
      });
    });
  });

  // BUG-1126: archiving from the chat menu sends no socket frame (Ragnarok
  // does not emit one), so the menu tells the list directly, for the lead the
  // request was for, whatever chat is open when the answer lands.
  leadActiveChangedEvent.useSubscription((change) => {
    activeChangeCountRef.current += 1;
    activeChangesRef.current.set(change.id, {
      ...change,
      count: activeChangeCountRef.current,
    });
    setLeads((prevLeads) =>
      withoutLeadsOutsideTab(withLeadActive(prevLeads, change), [change]),
    );
  });

  /** BUG-1127: the rows minus the changed leads that no longer belong to the open tab. */
  function withoutLeadsOutsideTab(rows: tChatLead[], changes: tLeadActiveChange[]) {
    const leaving = new Set(
      changes
        .filter((change) => !fitsActiveTab(selectedTab, change.active))
        .map(({ id }) => id),
    );
    return leaving.size ? rows.filter(({ id }) => !leaving.has(id)) : rows;
  }

  /**
   * Rows a list request answered, plus every `active` change made after it
   * started. BUG-1127: those changes also decide tab membership, and a page
   * that replaces the list (`replacesList`) takes back in the leads a frame
   * moved into the tab, placed by its own last row (`isLastPage`).
   */
  function keepLaterActiveChange(
    rows: tChatLead[],
    options: { countAtStart: number; replacesList?: { isLastPage: boolean } },
  ) {
    const later = [...activeChangesRef.current.values()].filter(
      (change) => change.count > options.countAtStart,
    );
    let nextRows = later.reduce(withLeadActive, rows);
    if (options.replacesList) {
      const { isLastPage: answeredLastPage } = options.replacesList;
      nextRows = later.reduce(
        (placed, change) =>
          change.row && fitsActiveTab(selectedTab, change.active)
            ? placeInActivityOrder(placed, {
                lead: { ...change.row, active: change.active },
                isLastPage: answeredLastPage,
              })
            : placed,
        nextRows,
      );
    }
    return withoutLeadsOutsideTab(nextRows, later);
  }

  useEffect(() => {
    undeliveredContinuationReloadedRef.current = false;
  }, [currentWorkspaceId, leadsInputField, selectedTab]);

  useEffect(() => {
    const requestId = ++listRequestIdRef.current;
    deletedDuringRequestRef.current.clear();
    // BUG-1126: every request that could still need a kept change is dropped
    // from here on (`listRequestIdRef`), and this one starts after them all.
    activeChangesRef.current.clear();
    loadingMoreRef.current = false;
    setNextCursor(null);
    setIsLastPage(false);

    (async () => {
      try {
        if (!currentWorkspaceId) {
          setLeads([]);
          setLeadsQuery('');
          setLoading(false);
          setSelectedTab(eChatLeadsTabs.ALL);
          setInputValue('');

          return;
        }

        setLoading(true);

        const queryParams = buildQueryParams();
        const activeChangesAtStart = activeChangeCountRef.current;

        const response = await MessengerService.searchLeads(
          currentWorkspaceId,
          queryParams,
        );

        if (requestId !== listRequestIdRef.current) return;

        undeliveredOldestLoadedRef.current =
          response.data.leads[response.data.leads.length - 1] ?? null;
        const visibleLeads = keepLaterActiveChange(
          response.data.leads.filter(({ id }) => !deletedDuringRequestRef.current.has(id)),
          {
            countAtStart: activeChangesAtStart,
            replacesList:
              selectedTab === eChatLeadsTabs.ACTIVE || selectedTab === eChatLeadsTabs.ARCHIVED
                ? { isLastPage: response.data.isLastPage || response.data.nextCursor == null }
                : undefined,
          },
        );
        setLeads(uniqBy(visibleLeads, 'id'));
        setLeadsQuery(leadsInputField);
        if (leadId && !deletedDuringRequestRef.current.has(leadId)) {
          // Find the full lead object in the response to get all properties (including handle)
          const foundLead = visibleLeads.find(
            (lead) => lead.id === leadId,
          );

          if (foundLead) {
            // Use the full lead object, but override integration if the query param
            // names a real channel; an unknown value is never cast into one (AGI-761).
            const leadWithIntegration = {
              ...foundLead,
              chat: {
                ...foundLead.chat,
                integration: resolveDeepLinkIntegration(
                  integration,
                  foundLead.chat?.integration || selectedLeadForChatModal?.chat?.integration,
                ),
              },
            };
            setSelectedLeadForChatModal(toChatModalLead(leadWithIntegration));
          } else {
            // Fallback: if lead not found in response, use minimal object. With no
            // known channel the access gate assigns the chat's default before the
            // chat mounts, so MessageBubbleV2 never sees an undefined channel
            // (CRITICAL-1; see resolveDeepLinkIntegration).
            const resolvedIntegration = resolveDeepLinkIntegration(
              integration,
              selectedLeadForChatModal?.chat?.integration,
            );
            // Not in the viewer's list, so nothing vouches for it: the main chat
            // checks access before mounting it (AGI-761).
            setSelectedLeadForChatModal(
              toLeadChatModalLead({ leadId, integration: resolvedIntegration }),
            );
          }
        } else if (!leadId && !inputValue.length && !selectedLeadForChatModal) {
          setSelectedLeadForChatModal(toChatModalLead(visibleLeads[0]));
        }

        setNextCursor(response.data.nextCursor ?? null);
        setIsLastPage(
          response.data.isLastPage || response.data.nextCursor == null,
        );
      } catch (err) {
        if (requestId !== listRequestIdRef.current) return;
        if (refusesUndeliveredFilter(err)) return;

        addAlert({
          type: eAlertType.ERROR,
          message:
            (err as Error).message ||
            'Error al acceder al servidor para traer la información de tus leads',
        });
      } finally {
        if (requestId === listRequestIdRef.current) {
          setLoading(false);
        }
      }
    })();
  }, [currentWorkspaceId, leadsInputField, selectedTab, refetchTrigger]);

  catchUpEvent.useSubscription(() => {
    setRefetchTrigger((prev) => prev + 1);
  });

  const isUndeliveredUnsupported =
    undeliveredCountState.workspaceId === currentWorkspaceId &&
    undeliveredCountState.unsupported;

  // INT-1500 U14: the filter only exists behind its flag, and not at all in a
  // workspace that refused it.
  useEffect(() => {
    if (
      selectedTab === eChatLeadsTabs.UNDELIVERED &&
      (!isUndeliveredListEnabled || isUndeliveredUnsupported)
    ) {
      setSelectedTab(eChatLeadsTabs.ALL);
    }
  }, [isUndeliveredListEnabled, isUndeliveredUnsupported, selectedTab]);

  // INT-1500 U14: a 422 on the "No entregados" list means the workspace has no
  // such filter (projection-backed leads). Back to "Todos", with no toast.
  function refusesUndeliveredFilter(error: unknown): boolean {
    if (
      selectedTab !== eChatLeadsTabs.UNDELIVERED ||
      !isUndeliveredFilterRefused(error)
    ) {
      return false;
    }
    setUndeliveredCountState({
      workspaceId: currentWorkspaceId,
      count: null,
      unsupported: true,
    });
    setSelectedTab(eChatLeadsTabs.ALL);
    return true;
  }

  // INT-1500 U14 (contract C9): the "No entregados (N)" count, asked again
  // whenever the list is (workspace, search, tab, reconnection) and, throttled,
  // on lead updates. A count that cannot be read (424, or 422 where the filter
  // does not exist) hides the chip: never a zero, never a toast.
  useEffect(() => () => clearTimeout(undeliveredCountThrottleRef.current), []);

  useEffect(() => {
    if (!isUndeliveredListEnabled || !currentWorkspaceId) return undefined;
    let isCurrent = true;
    const workspaceId = currentWorkspaceId;

    MessengerService.getUndeliveredCount(workspaceId, {
      ...(leadsInputField && { query: leadsInputField }),
    })
      .then((count) => {
        if (!isCurrent) return;
        // A workspace that refused the filter keeps refusing it: a count that
        // answers later does not bring back a chip whose list would 422.
        setUndeliveredCountState((previous) =>
          previous.workspaceId === workspaceId && previous.unsupported
            ? previous
            : { workspaceId, count, unsupported: false },
        );
      })
      .catch((error) => {
        if (!isCurrent) return;
        const unsupported = isUndeliveredFilterRefused(error);
        // Same outcome as the last failure: keep the state object, so a count
        // that keeps failing does not re-render the list on every refresh.
        setUndeliveredCountState((previous) =>
          previous.workspaceId === workspaceId &&
          previous.count === null &&
          previous.unsupported === unsupported
            ? previous
            : { workspaceId, count: null, unsupported },
        );
      });

    return () => {
      isCurrent = false;
    };
  }, [
    isUndeliveredListEnabled,
    currentWorkspaceId,
    leadsInputField,
    selectedTab,
    refetchTrigger,
    undeliveredCountTick,
  ]);

  const undeliveredCount =
    isUndeliveredListEnabled &&
    undeliveredCountState.workspaceId === currentWorkspaceId &&
    !undeliveredCountState.unsupported
      ? undeliveredCountState.count
      : null;

  async function getMoreLeads() {
    const requestId = listRequestIdRef.current;

    try {
      if (
        !currentWorkspaceId ||
        isLastPage ||
        !nextCursor ||
        loadingMoreRef.current
      ) {
        return;
      }

      loadingMoreRef.current = true;
      const queryParams = buildQueryParams(nextCursor);
      const activeChangesAtStart = activeChangeCountRef.current;

      // Keep the whole cursor chain on the same endpoint as its first request.
      // This is especially important for search: changing to `/filter` on the
      // second page silently dropped the search predicate in the legacy flow.
      const response = await MessengerService.searchLeads(
        currentWorkspaceId,
        queryParams,
      );

      if (requestId !== listRequestIdRef.current) return;

      undeliveredContinuationReloadedRef.current = false;
      undeliveredOldestLoadedRef.current =
        response.data.leads[response.data.leads.length - 1] ??
        undeliveredOldestLoadedRef.current;
      setNextCursor(response.data.nextCursor ?? null);
      setIsLastPage(
        response.data.isLastPage || response.data.nextCursor == null,
      );
      const nextLeads = keepLaterActiveChange(
        response.data.leads.filter(({ id }) => !deletedDuringRequestRef.current.has(id)),
        { countAtStart: activeChangesAtStart },
      );
      setLeads((prevLeads) => uniqBy([...prevLeads, ...nextLeads], 'id'));
    } catch (err) {
      if (requestId !== listRequestIdRef.current) return;
      // INT-1538 U7 (KTD9): on a continuation a 422 is a cursor of another
      // order (INT-1538 changed it), not a workspace without the filter:
      // that refusal only shows on page 1 and on the count. Reload page 1
      // once, keeping the tab and the chip.
      if (
        selectedTab === eChatLeadsTabs.UNDELIVERED &&
        isUndeliveredFilterRefused(err) &&
        !undeliveredContinuationReloadedRef.current
      ) {
        undeliveredContinuationReloadedRef.current = true;
        setRefetchTrigger((prev) => prev + 1);
        return;
      }

      addAlert({
        type: eAlertType.ERROR,
        message:
          (err as Error).message ||
          'Error al acceder al servidor para traer la información de tus leads',
      });
    } finally {
      if (requestId === listRequestIdRef.current) {
        loadingMoreRef.current = false;
      }
    }
  }

  return {
    selectedTab,
    inputValue,
    loading,
    leads,
    leadsQuery,
    isLastPage,
    searchInputRef,
    setSelectedTab,
    setInputValue,
    setLoading,
    setLeads,
    getMoreLeads,
    undeliveredCount,
  };
}
